import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarClock, X, Check, Clock } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

const SLOTS = ["9:00 AM", "10:00 AM", "11:00 AM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"];

const statusTone = {
  confirmed: "bg-emerald-100 text-emerald-700",
  requested: "bg-amber-100 text-amber-700",
  cancelled: "bg-slate-100 text-slate-500 line-through",
};

export default function Appointments() {
  const [appts, setAppts] = useState([]);
  const [date, setDate] = useState(new Date());
  const [time, setTime] = useState("");
  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/portal/appointments").then(({ data }) => setAppts(data));
  useEffect(() => { load(); }, []);

  const book = async (e) => {
    e.preventDefault();
    if (!date || !time || !topic) {
      toast.error("Please choose a date, time and topic.");
      return;
    }
    setSubmitting(true);
    try {
      const iso = date.toISOString().slice(0, 10);
      await api.post("/portal/appointments", { date: iso, time, topic, notes });
      toast.success("Consultation requested. Your advisor will confirm shortly.");
      setTopic(""); setNotes(""); setTime("");
      load();
    } catch {
      toast.error("Could not book. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (id) => {
    await api.delete(`/portal/appointments/${id}`);
    toast.info("Appointment cancelled.");
    load();
  };

  return (
    <div data-testid="portal-appointments">
      <div className="mb-8">
        <p className="overline text-primary">Appointments</p>
        <h1 className="font-serif text-4xl text-slate-900 mt-1">Book a consultation</h1>
        <p className="text-slate-500 mt-2">Select a date and time to meet with your Sagos advisor.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <form onSubmit={book} data-testid="appointment-form" className="rounded-2xl border border-border bg-white p-6">
          <div className="flex justify-center">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              className="rounded-md"
            />
          </div>

          <div className="mt-5">
            <Label className="mb-2 block">Time slot</Label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {SLOTS.map((t) => (
                <button
                  type="button"
                  key={t}
                  data-testid={`slot-${t.replace(/[:\s]/g, "")}`}
                  onClick={() => setTime(t)}
                  className={`px-2 py-2 rounded-lg text-sm border transition-colors duration-200 ${
                    time === t ? "bg-primary text-primary-foreground border-primary" : "border-border text-slate-600 hover:border-primary"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-5 space-y-2">
            <Label htmlFor="topic">Topic</Label>
            <Input id="topic" data-testid="appointment-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Portfolio review" />
          </div>
          <div className="mt-4 space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea id="notes" data-testid="appointment-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything you'd like to cover?" />
          </div>

          <button type="submit" disabled={submitting} data-testid="appointment-submit" className="mt-5 w-full py-3 rounded-full bg-primary text-primary-foreground font-medium hover:bg-[#0b2a5b] transition-colors duration-200 disabled:opacity-60">
            {submitting ? "Requesting…" : "Request Consultation"}
          </button>
        </form>

        <div className="rounded-2xl border border-border bg-white p-6">
          <h3 className="font-serif text-2xl text-slate-900 flex items-center gap-2"><CalendarClock size={20} className="text-primary" /> Your appointments</h3>
          <div className="mt-5 space-y-3">
            {appts.length === 0 && <p className="text-slate-400 text-sm">No appointments yet.</p>}
            {appts.map((a) => (
              <div key={a.id} data-testid={`appt-${a.id}`} className="rounded-xl border border-border p-4 flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-800">{a.topic}</p>
                    <Badge className={`${statusTone[a.status] || ""} border-0 font-normal capitalize`}>{a.status}</Badge>
                  </div>
                  <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5"><Clock size={13} /> {a.date} · {a.time}</p>
                  {a.notes && <p className="text-sm text-slate-400 mt-1">{a.notes}</p>}
                </div>
                {a.status !== "cancelled" && (
                  <button data-testid={`appt-cancel-${a.id}`} onClick={() => cancel(a.id)} className="text-slate-300 hover:text-destructive transition-colors" title="Cancel">
                    <X size={18} />
                  </button>
                )}
                {a.status === "confirmed" && <Check size={0} />}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
